import { Box, Breadcrumb, BreadcrumbLink, Heading } from "@chakra-ui/react"
import { Fragment, useEffect, useRef } from "react"
import { useRouteMatch } from "react-router-dom"
import Link from "../link/Link"
import Pathicon from "../pathicon/Pathicon"
import {
  AugmentationToolbarDropdown,
  AugmentationToolbarHookReturn,
  AugmentationToolbarOptionNode,
  useOptionLabel
} from "./AugmentationToolbar"

function AugmentationToolbarBreadcrumb({
  toolbar,
  option
}: {
  toolbar: AugmentationToolbarHookReturn
  option: AugmentationToolbarOptionNode
}) {
  const index = toolbar.stack.indexOf(option)
  const isLast = index === toolbar.stack.length - 1
  // Form the route to match based on the toolbar stack up to this option.
  const route = `/${toolbar.stack
    .slice(0, index + 1)
    .map((o) => o.path || o.key)
    .join("/")}/`
  // We can use the match.url to route to this toolbar option.
  const match = useRouteMatch(route)
  const label = useOptionLabel(option)

  // The stack is set from an effect, so for one commit after navigating the
  // trail is asked to render against a location it no longer matches. Dropping
  // the crumb for that frame is what makes the header jump on the way back to
  // the root: the trail loses a crumb, then the whole thing swaps to the root
  // heading. Holding the last match it resolved keeps the trail whole until the
  // stack catches up, so the header changes once instead of twice.
  const lastMatch = useRef(match)
  useEffect(() => {
    if (match) lastMatch.current = match
  }, [match])

  const resolvedMatch = match || lastMatch.current
  if (!resolvedMatch) return null

  const crumbLabel = option.AugmentationLabelComponent ? (
    <option.AugmentationLabelComponent match={resolvedMatch} />
  ) : (
    label
  )

  return (
    // The heading wraps its link rather than the reverse, so the crumb reads as
    // a heading containing a link.
    <Heading
      as="h2"
      aria-current={isLast ? "page" : undefined}
      color="#000"
      fontSize="16px"
      fontWeight={600}
      lineHeight="19px"
      m={0}
      minW={0}
      overflow="hidden"
      // Truncated on one line rather than clamped: a clamp still lets a crumb
      // wrap mid-word before it hides the overflow.
      textOverflow="ellipsis"
      whiteSpace="nowrap"
      w="100%"
    >
      {isLast ? (
        <Box
          as="span"
          overflow="hidden"
          textDecoration="none"
          textOverflow="ellipsis"
        >
          {crumbLabel}
        </Box>
      ) : (
        <Link
          as={BreadcrumbLink}
          to={resolvedMatch.url}
          color="inherit"
          display="block"
          fontFamily="inherit"
          fontSize="inherit"
          fontWeight="inherit"
          lineHeight="inherit"
          minW={0}
          overflow="hidden"
          textOverflow="ellipsis"
          variant="plain"
        >
          {crumbLabel}
        </Link>
      )}
    </Heading>
  )
}

function AugmentationToolbarBreadcrumbs(
  toolbar: AugmentationToolbarHookReturn
) {
  // A level can exist in the route tree without being anywhere you can stand —
  // the license offering embeds nest their forms under a `Manage` path that has
  // no panel view of its own. Naming it in the trail offers a step back to a
  // screen that isn't there, so the trail names only the levels that are.
  //
  // The trail still appears or not based on the whole stack: an option's
  // children are its subtree whether or not it draws anything itself, and
  // collapsing to the root dropdown because the only parent was structural
  // would swap the header for something that can't go back.
  const crumbs = toolbar.stack.filter((option) => option.AugmentationComponent)

  return toolbar.stack.length > 1 ? (
    <Breadcrumb.Root
      // We'll let the global theme handle side padding
      py={0}
      gap="5px"
      flex="1"
      minW={0}
      overflow="hidden"
      css={{
        ["& [role='presentation']"]: {
          display: "flex"
        }
      }}
    >
      {/* Legacy settings embeds reach four levels deep, well past the two this
      was built for, so the trail truncates rather than wraps at panel width. */}
      <Breadcrumb.List
        flexWrap="nowrap"
        minW={0}
        overflow="hidden"
        p={0}
        w="100%"
      >
        {crumbs.map((option, i) => {
          return (
            <Fragment key={option.key}>
              {i > 0 && (
                <Breadcrumb.Separator display="flex" flexShrink={0}>
                  <Pathicon icon="chevron-right" size={12} />
                </Breadcrumb.Separator>
              )}
              <Breadcrumb.Item
                flex="0 1 auto"
                gap={0}
                minW={0}
                overflow="hidden"
              >
                <AugmentationToolbarBreadcrumb
                  toolbar={toolbar}
                  option={option}
                />
              </Breadcrumb.Item>
            </Fragment>
          )
        })}
      </Breadcrumb.List>
    </Breadcrumb.Root>
  ) : (
    <AugmentationToolbarDropdown {...toolbar} />
  )
}

export default AugmentationToolbarBreadcrumbs
